import React, { useState, useEffect, useCallback } from 'react';
import { signInAnonymously } from 'firebase/auth';
import { collection, getDocs, doc, updateDoc, writeBatch } from 'firebase/firestore';
import { db, auth } from '../services/firebase';
import './RaceRequestsPage.css';

interface RaceRequest {
  id: string;
  userEmail?: string;
  timestamp: any;
  raceName?: string;
  cityCountry?: string;
  raceDate?: any;
  distance?: string;
  status?: string;
  emailSent?: boolean;
}

type Tab = 'requests' | 'emails';

const PASSWORD = 'ryleehume';
const STORAGE_KEY = 'race_requests_unlocked';
const CUTOFF_DATE = new Date('2026-06-29T00:00:00');

const formatDateTime = (timestamp: any): string => {
  if (!timestamp) return '—';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
};

const formatDate = (timestamp: any): string => {
  if (!timestamp) return '—';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
};

const getRaceDateTime = (r: RaceRequest): number =>
  r.raceDate ? (r.raceDate.toDate ? r.raceDate.toDate().getTime() : new Date(r.raceDate).getTime()) : 0;

const ensureAuth = async () => {
  if (!auth.currentUser) await signInAnonymously(auth);
};

// ── Reusable table component ──────────────────────────────────────────────────

interface RequestTableProps {
  rows: RaceRequest[];
  selected: Set<string>;
  processing: Set<string>;
  actionLabel: string;
  onToggle: (id: string) => void;
  onToggleAll: () => void;
  onAction: (id: string) => void;
  onBulkAction: () => void;
  showEmail?: boolean;
  showRequestDate?: boolean;
}

const RequestTable: React.FC<RequestTableProps> = ({
  rows, selected, processing, actionLabel,
  onToggle, onToggleAll, onAction, onBulkAction,
  showEmail = true, showRequestDate = true,
}) => {
  const allSelected = rows.length > 0 && rows.every(r => selected.has(r.id));

  return (
    <>
      {selected.size > 0 && (
        <div className="rr-bulk-bar">
          <span>{selected.size} selected</span>
          <button className="rr-bulk-btn" onClick={onBulkAction}>
            {actionLabel} ({selected.size})
          </button>
        </div>
      )}
      <div className="rr-table-wrapper">
        <table className="rr-table">
          <thead>
            <tr>
              <th className="rr-th-check">
                <input type="checkbox" checked={allSelected} onChange={onToggleAll} />
              </th>
              {showRequestDate && <th>Request Date</th>}
              {showEmail && <th>Email</th>}
              <th>Race Name</th>
              <th>City / Country</th>
              <th>Race Date</th>
              <th>Distance</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className={selected.has(r.id) ? 'rr-row-selected' : ''}>
                <td className="rr-td-check">
                  <input
                    type="checkbox"
                    checked={selected.has(r.id)}
                    onChange={() => onToggle(r.id)}
                  />
                </td>
                {showRequestDate && <td className="rr-cell-date">{formatDateTime(r.timestamp)}</td>}
                {showEmail && <td className="rr-cell-email">{r.userEmail ?? '—'}</td>}
                <td className="rr-cell-name">{r.raceName ?? '—'}</td>
                <td>{r.cityCountry ?? '—'}</td>
                <td>{formatDate(r.raceDate)}</td>
                <td>{r.distance ?? '—'}</td>
                <td className="rr-cell-action">
                  <button
                    className="rr-done-btn"
                    onClick={() => onAction(r.id)}
                    disabled={processing.has(r.id)}
                  >
                    {processing.has(r.id) ? <span className="rr-done-spinner" /> : actionLabel}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────

const RaceRequestsPage: React.FC = () => {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [activeTab, setActiveTab] = useState<Tab>('requests');

  // Requests tab
  const [requests, setRequests] = useState<RaceRequest[]>([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [selectedRequests, setSelectedRequests] = useState<Set<string>>(new Set());
  const [processingRequests, setProcessingRequests] = useState<Set<string>>(new Set());

  // Emails tab
  const [emails, setEmails] = useState<RaceRequest[]>([]);
  const [emailsLoading, setEmailsLoading] = useState(false);
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());
  const [processingEmails, setProcessingEmails] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY) === 'true') setIsUnlocked(true);
  }, []);

  useEffect(() => {
    if (isUnlocked) {
      fetchRequests();
      fetchEmails();
    }
  }, [isUnlocked]);

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordInput !== PASSWORD) {
      setPasswordError('Incorrect password');
      setPasswordInput('');
      return;
    }
    try {
      await signInAnonymously(auth);
      localStorage.setItem(STORAGE_KEY, 'true');
      setIsUnlocked(true);
      setPasswordError('');
    } catch (err) {
      console.error('Auth error:', err);
      setPasswordError('Authentication failed. Please try again.');
    }
  };

  const fetchRequests = async () => {
    setRequestsLoading(true);
    try {
      await ensureAuth();
      const snapshot = await getDocs(collection(db, 'raceRequests'));
      const data = snapshot.docs
        .map(d => ({ id: d.id, ...d.data() } as RaceRequest))
        .filter(r => {
          if (r.status === 'done') return false;
          if (!r.timestamp) return false;
          const ts = r.timestamp.toDate ? r.timestamp.toDate() : new Date(r.timestamp);
          return ts >= CUTOFF_DATE;
        })
        .sort((a, b) => getRaceDateTime(a) - getRaceDateTime(b));
      setRequests(data);
    } catch (err) {
      console.error('Error fetching requests:', err);
    } finally {
      setRequestsLoading(false);
    }
  };

  const fetchEmails = async () => {
    setEmailsLoading(true);
    try {
      await ensureAuth();
      const snapshot = await getDocs(collection(db, 'raceRequests'));
      const data = snapshot.docs
        .map(d => ({ id: d.id, ...d.data() } as RaceRequest))
        .filter(r => {
          if (r.status !== 'done') return false;
          if (r.emailSent === true) return false;
          if (!r.timestamp) return false;
          const ts = r.timestamp.toDate ? r.timestamp.toDate() : new Date(r.timestamp);
          return ts >= CUTOFF_DATE;
        })
        .sort((a, b) => getRaceDateTime(a) - getRaceDateTime(b));
      setEmails(data);
    } catch (err) {
      console.error('Error fetching emails:', err);
    } finally {
      setEmailsLoading(false);
    }
  };

  // ── Mark done ───────────────────────────────────────────────────────────────

  const markDone = async (id: string) => {
    setProcessingRequests(prev => new Set(prev).add(id));
    try {
      await updateDoc(doc(db, 'raceRequests', id), { status: 'done' });
      setRequests(prev => prev.filter(r => r.id !== id));
      setSelectedRequests(prev => { const n = new Set(prev); n.delete(id); return n; });
      fetchEmails(); // refresh emails tab
    } catch (err) {
      console.error('Error marking done:', err);
    } finally {
      setProcessingRequests(prev => { const n = new Set(prev); n.delete(id); return n; });
    }
  };

  const markDoneBulk = async () => {
    const ids = Array.from(selectedRequests);
    if (!ids.length) return;
    setProcessingRequests(new Set(ids));
    try {
      const batch = writeBatch(db);
      ids.forEach(id => batch.update(doc(db, 'raceRequests', id), { status: 'done' }));
      await batch.commit();
      setRequests(prev => prev.filter(r => !ids.includes(r.id)));
      setSelectedRequests(new Set());
      fetchEmails();
    } catch (err) {
      console.error('Error bulk marking done:', err);
    } finally {
      setProcessingRequests(new Set());
    }
  };

  // ── Mark emailed ────────────────────────────────────────────────────────────

  const markEmailed = async (id: string) => {
    setProcessingEmails(prev => new Set(prev).add(id));
    try {
      await updateDoc(doc(db, 'raceRequests', id), { emailSent: true });
      setEmails(prev => prev.filter(r => r.id !== id));
      setSelectedEmails(prev => { const n = new Set(prev); n.delete(id); return n; });
    } catch (err) {
      console.error('Error marking emailed:', err);
    } finally {
      setProcessingEmails(prev => { const n = new Set(prev); n.delete(id); return n; });
    }
  };

  const markEmailedBulk = async () => {
    const ids = Array.from(selectedEmails);
    if (!ids.length) return;
    setProcessingEmails(new Set(ids));
    try {
      const batch = writeBatch(db);
      ids.forEach(id => batch.update(doc(db, 'raceRequests', id), { emailSent: true }));
      await batch.commit();
      setEmails(prev => prev.filter(r => !ids.includes(r.id)));
      setSelectedEmails(new Set());
    } catch (err) {
      console.error('Error bulk marking emailed:', err);
    } finally {
      setProcessingEmails(new Set());
    }
  };

  // ── Selection helpers ───────────────────────────────────────────────────────

  const toggleRequest = useCallback((id: string) => {
    setSelectedRequests(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }, []);

  const toggleAllRequests = useCallback(() => {
    setSelectedRequests(prev => prev.size === requests.length ? new Set() : new Set(requests.map(r => r.id)));
  }, [requests]);

  const toggleEmail = useCallback((id: string) => {
    setSelectedEmails(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }, []);

  const toggleAllEmails = useCallback(() => {
    setSelectedEmails(prev => prev.size === emails.length ? new Set() : new Set(emails.map(r => r.id)));
  }, [emails]);

  // ── Password screen ─────────────────────────────────────────────────────────

  if (!isUnlocked) {
    return (
      <div className="rr-lock-screen">
        <div className="rr-lock-card">
          <h2 className="rr-lock-title">Race Requests</h2>
          <form onSubmit={handlePasswordSubmit}>
            <input
              className="rr-lock-input"
              type="password"
              value={passwordInput}
              onChange={e => setPasswordInput(e.target.value)}
              placeholder="Password"
              autoFocus
            />
            {passwordError && <p className="rr-lock-error">{passwordError}</p>}
            <button className="rr-lock-btn" type="submit">Unlock</button>
          </form>
        </div>
      </div>
    );
  }

  // ── Main UI ─────────────────────────────────────────────────────────────────

  return (
    <div className="rr-page">
      <div className="rr-hero">
        <h1 className="rr-hero-rally">Rally</h1>
        <p className="rr-hero-tagline">Voice Notes on Race Day</p>
      </div>

      <div className="rr-tabs">
        <button
          className={`rr-tab${activeTab === 'requests' ? ' rr-tab--active' : ''}`}
          onClick={() => setActiveTab('requests')}
        >
          Race Requests
          {requests.length > 0 && <span className="rr-tab-badge">{requests.length}</span>}
        </button>
        <button
          className={`rr-tab${activeTab === 'emails' ? ' rr-tab--active' : ''}`}
          onClick={() => setActiveTab('emails')}
        >
          Emails
          {emails.length > 0 && <span className="rr-tab-badge">{emails.length}</span>}
        </button>
      </div>

      {/* Race Requests tab */}
      {activeTab === 'requests' && (
        <>
          {requestsLoading && <div className="rr-loading"><div className="rr-spinner" /></div>}
          {!requestsLoading && requests.length === 0 && <div className="rr-empty">No pending race requests.</div>}
          {!requestsLoading && requests.length > 0 && (
            <RequestTable
              rows={requests}
              selected={selectedRequests}
              processing={processingRequests}
              actionLabel="Done"
              onToggle={toggleRequest}
              onToggleAll={toggleAllRequests}
              onAction={markDone}
              onBulkAction={markDoneBulk}
            />
          )}
        </>
      )}

      {/* Emails tab */}
      {activeTab === 'emails' && (
        <>
          {emailsLoading && <div className="rr-loading"><div className="rr-spinner" /></div>}
          {!emailsLoading && emails.length === 0 && <div className="rr-empty">No emails to send.</div>}
          {!emailsLoading && emails.length > 0 && (
            <RequestTable
              rows={emails}
              selected={selectedEmails}
              processing={processingEmails}
              actionLabel="Mark Emailed"
              onToggle={toggleEmail}
              onToggleAll={toggleAllEmails}
              onAction={markEmailed}
              onBulkAction={markEmailedBulk}
              showRequestDate={false}
            />
          )}
        </>
      )}
    </div>
  );
};

export default RaceRequestsPage;
